import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ImageUploadField, PageHeader, useToast } from 'components/admin';
import { Button, Checkbox, Input, Select, Textarea } from 'components/common';
import RichTextEditor from 'components/admin/RichTextEditor';
import { createNews, getAdminNewsById, updateNews } from 'apis/adminNews';
import { getNewsCategories } from 'apis/newsCategories';
import { useActiveLanguagesQuery, useAdminCreateMutation, useAdminDetailQuery, useAdminListQuery, useAdminUpdateMutation } from 'hooks';
import '../../../styles/admin-shared.scss';


const NewsForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState(0);
    const [formData, setFormData] = useState({
        newsCategoryId: '',
        sortOrder: 0,
        isActive: true,
        translation: [],
        images: []
    });

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: categoriesData = { items: [] } } = useAdminListQuery('news-categories-options', { pageSize: 1000 }, getNewsCategories);
    const categories = categoriesData.items;
    const { data: detail, error: detailError } = useAdminDetailQuery('news', id, getAdminNewsById);
    const createMutation = useAdminCreateMutation('news', createNews);
    const updateMutation = useAdminUpdateMutation('news', updateNews);
    const loading = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (!id && languages.length) {
            setFormData(prev => ({
                ...prev,
                translation: languages.map(l => ({ languageId: l.id, title: '', slug: '', summary: '', description: '' })),
            }));
        }
    }, [id, languages]);

    useEffect(() => {
        if (detail) {
            setFormData({ newsCategoryId: detail.newsCategoryId || '', sortOrder: detail.sortOrder || 0, isActive: detail.isActive ?? true, translation: detail.translation || [], images: detail.images || [] });
        }
    }, [detail]);

    useEffect(() => { if (detailError) toast.error(detailError.message || 'T?i d? li?u th?t b?i'); }, [detailError, toast]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleTranslationChange = (langId, field, value) => {
        setFormData(prev => ({
            ...prev,
            translation: prev.translation.map(t => t.languageId === langId ? { ...t, [field]: value } : t),
        }));
    };

    const handleAddImage = () => {
        setFormData(prev => ({ ...prev, images: [...prev.images, { imageUrl: '', sortOrder: 0 }] }));
    };

    const handleImageChange = (index, field, value) => {
        setFormData(prev => {
            const newImages = [...prev.images];
            newImages[index] = { ...newImages[index], [field]: field === 'sortOrder' ? Number(value) : value };
            return { ...prev, images: newImages };
        });
    };

    const handleRemoveImage = (index) => {
        setFormData(prev => {
            const newImages = [...prev.images];
            newImages.splice(index, 1);
            return { ...prev, images: newImages };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...formData, newsCategoryId: Number(formData.newsCategoryId), sortOrder: Number(formData.sortOrder) };
            const res = id ? await updateMutation.mutateAsync({ id, data: payload }) : await createMutation.mutateAsync(payload);
            if (res.success) { toast.success(id ? 'Cập nhật thành công' : 'Thêm mới thành công'); navigate('/admin/news'); }
            else { if (res.messageError) setErrors(res.messageError); else toast.error(res.message || 'Lưu thất bại'); }
        } catch { toast.error('Lưu thất bại'); }
    };

    return (
        <div className="admin-page">
            <PageHeader title={id ? 'Cập nhật Tin Tức' : 'Thêm Tin Tức'} />
            <form className="form-card" onSubmit={handleSubmit}>
                <p className="form-section-title">Cài đặt Bài viết</p>
                <div className="form-row">
                    <Select
                        label="Danh mục Tin tức *"
                        name="newsCategoryId"
                        value={formData.newsCategoryId}
                        onChange={handleChange}
                        options={categories.filter(c => c.isActive).map(c => {
                            const activeLangId = languages[activeTab]?.id;
                            const t = c.translations?.find(tr => tr.languageId === activeLangId) || c.translations?.[0];
                            return { value: c.id, label: t?.title || c.title || `Danh mục ${c.id}` };
                        })}
                        placeholder="Chọn danh mục..."
                        required
                        error={errors.newsCategoryId}
                    />
                    <Input label="Thứ tự sắp xếp" name="sortOrder" type="number" value={formData.sortOrder} onChange={handleChange} />
                </div>
                <Checkbox label="Hoạt động" name="isActive" checked={formData.isActive} onChange={handleChange} />

                <p className="form-section-title">Hình ảnh</p>
                {formData.images.map((img, idx) => (
                    <div key={idx} className="form-row" style={{ alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                            <ImageUploadField
                                label="Hình ảnh *"
                                value={img.imageUrl}
                                folder="news"
                                onChange={(val) => handleImageChange(idx, 'imageUrl', val)}
                            />
                        </div>
                        <div style={{ width: 120 }}>
                            <Input label="Thứ tự" type="number" value={img.sortOrder} onChange={e => handleImageChange(idx, 'sortOrder', e.target.value)} />
                        </div>
                        <Button type="button" variant="danger" onClick={() => handleRemoveImage(idx)} style={{ marginTop: 28 }}>Xóa</Button>
                    </div>
                ))}
                <div style={{ marginBottom: 20 }}>
                    <Button type="button" variant="secondary" onClick={handleAddImage}>+ Thêm Hình Ảnh</Button>
                </div>

                {languages.length > 0 && (
                    <>
                        <p className="form-section-title">Bản dịch</p>
                        <div className="translation-tabs">
                            <div className="tab-header">
                                {languages.map((lang, i) => (
                                    <button key={lang.id} type="button" className={`tab-btn ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>
                                        {lang.name} ({lang.code})
                                    </button>
                                ))}
                            </div>
                            {languages.map((lang, i) => {
                                const t = formData.translation.find(t => t.languageId === lang.id) || {};
                                return activeTab === i ? (
                                    <div key={lang.id} className="tab-content">
                                        <Input label="Tiêu đề *" value={t.title || ''} onChange={e => handleTranslationChange(lang.id, 'title', e.target.value)} required />
                                        <Input label="Slug" value={t.slug || ''} onChange={e => handleTranslationChange(lang.id, 'slug', e.target.value)} />
                                        <Textarea label="Tóm tắt" value={t.summary || ''} onChange={e => handleTranslationChange(lang.id, 'summary', e.target.value)} rows={3} />
                                        <div className="input-group" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                                            <label style={{ marginBottom: '0.5rem', display: 'block', fontWeight: 500 }}>Mô tả</label>
                                            <RichTextEditor 
                                                value={t.description || ''} 
                                                onChange={value => handleTranslationChange(lang.id, 'description', value)} 
                                            />
                                        </div>
                                    </div>
                                ) : null;
                            })}
                        </div>
                    </>
                )}

                <div className="form-actions">
                    <Button type="submit" disabled={loading}>{loading ? 'Đang lưu...' : id ? 'Cập nhật' : 'Thêm mới'}</Button>
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/news')}>Hủy</Button>
                </div>
            </form>
        </div>
    );
};

export default NewsForm;
